/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import cx from 'classnames';
import Image from '@/components/NextImage';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { getPriceArray } from '../../utils/getPriceArray';
import type { DealConstraintSkuDetails, DealConstraint } from '../../../interface';
import styles from './BundleDealConstraintCardV2.module.scss';
import { useFeatureFlag } from '@/features/globalConfig';
import { Icon } from '@az/starc-ui';
import { SwapItem } from '@/components/MyAccount/Icons/Icons';
import Price from './Price';
import CheckIcon from '@/public/images/check.svg';
import iconStyles from '@/theme/iconStyles.module.scss';
import { eventConstants } from '@/constants/event';

type Props = {
  constraint: DealConstraint;
  skuDetails: DealConstraintSkuDetails;
  selected?: boolean;
  isCurrent?: boolean;
  onClick?: () => void;
  className?: string;
  onSwapClick?: () => void;
  showSwapButton?: boolean;
  tabIndex?: number;
};
const labelMap = {
  label_deal_bundle_contraint_item_quantity: 'label_deal_bundle_contraint_item_quantity',
  label_deals_sku_location: 'label_deals_sku_location',
  lblProductPartNumber: 'label_Deals_DealsPage_PartNo',
  lblSwapItem: 'label_deals_swap_item',
  lblCurrentItem: 'label_cart_current_item',
  productNotesLabel: 'label_dealsDrawer_productNotes',
  lugHoleQuantity: 'label_dealDrawer_lugHole',
  mountingHardwareIncluded: 'label_DealDrawer_mountingHardware',
  padType: 'label_dealDrawer_Padtype',
};

export const BundleDealConstraintCardV2 = ({
  constraint,
  skuDetails,
  onSwapClick,
  showSwapButton,
  selected,
  isCurrent,
  onClick,
  className,
  tabIndex = -1,
}: Props) => {
  const { minQty } = constraint;
  const {
    description,
    productImageUrl,
    productTotalPrice,
    discountPrice,
    locationFilter,
    discountAmount,
    lugHoleQuantity,
    productNotes,
    productAttributes,
  } = skuDetails;
  const labels = useLabels(labelMap);
  const locale = useLocale();

  const descriptionLower = (description || '').toLowerCase();
  const isBrakePad =
    descriptionLower.includes('brake pad') ||
    descriptionLower.includes('brake pads') ||
    descriptionLower.includes('balatas');
  const isBrakeRotor = descriptionLower.includes('rotor') || descriptionLower.includes('rotors');

  const mountingHardwareIncluded = productAttributes?.find(
    (attr) => attr.name === 'Mounting Hardware Included',
  )?.value;

  const padType = productAttributes?.find(
    (attr) => attr.name === 'Friction Material Composition',
  )?.value;

  const studLugHoleQuantity =
    productAttributes?.find(
      (attr) =>
        attr.name === 'Stud/Lug Hole Quantity' || attr.name === 'cantidad de birlos/orificios',
    )?.value || lugHoleQuantity?.toString();
  const validDiscountPrice = discountPrice != null;

  const discountAndTotalAreDifferent = validDiscountPrice && discountPrice !== productTotalPrice;
  const isFree = discountAmount === productTotalPrice;
  const hasSomeOfDiscountAmount = discountAmount > 0 && discountAmount < productTotalPrice;
  const hasDealPrice = discountAndTotalAreDifferent && !isFree;
  const isNewDealDiscountExperienceEnabled =
    useFeatureFlag('NEW_DEAL_DISCOUNT_EXPERIENCE_ENABLED') === 'true';

  const onKeyDownHandler = (e: React.KeyboardEvent) => {
    if (e.key === eventConstants.enterKeyType || e.key === eventConstants.spaceKeyType) {
      e.preventDefault();
      onClick?.();
    }
  };

  const onSwapKeyDownHandler = (e: React.KeyboardEvent) => {
    if (e.key === eventConstants.enterKeyType || e.key === eventConstants.spaceKeyType) {
      e.preventDefault();
      onSwapClick?.();
    }
  };

  const getTotalPrice = () => {
    let totalPrice = productTotalPrice;
    if (hasDealPrice) {
      totalPrice = discountPrice;
    }
    return totalPrice;
  };

  const getTotalPriceNewDeal = () => {
    let totalPrice = productTotalPrice;
    if (isFree) {
      totalPrice = 0;
    } else if (hasSomeOfDiscountAmount) {
      totalPrice = discountAmount;
    }
    return totalPrice;
  };

  const getStrikeThroughPrice = () => {
    let strikeThroughPrice = undefined;
    if (hasDealPrice) {
      strikeThroughPrice = productTotalPrice;
    }
    return strikeThroughPrice;
  };

  const getStrikeThroughPriceNewDeal = () => {
    let strikeThroughPrice = undefined;
    if (isFree) {
      strikeThroughPrice = productTotalPrice;
    }
    return strikeThroughPrice;
  };

  const getDealPriceAndWasDealPrice = (): {
    wasDealPrice: string[] | undefined;
    dealPrice: string[];
  } => {
    const prices = {
      strikeThroughPrice: isNewDealDiscountExperienceEnabled
        ? getStrikeThroughPriceNewDeal()
        : getStrikeThroughPrice(),
      totalPrice: isNewDealDiscountExperienceEnabled ? getTotalPriceNewDeal() : getTotalPrice(),
    };
    return {
      wasDealPrice: prices.strikeThroughPrice
        ? getPriceArray(prices.strikeThroughPrice)
        : undefined,
      dealPrice: getPriceArray(prices.totalPrice),
    };
  };

  const { dealPrice, wasDealPrice } = getDealPriceAndWasDealPrice();

  return (
    <>
      <div
        role="radio"
        aria-checked={selected}
        tabIndex={tabIndex}
        onClick={onClick}
        onKeyDown={onKeyDownHandler}
        className={cx(styles.root, className, { [styles.selected]: selected })}
      >
        <div className={styles.product}>
          <Image src={productImageUrl} alt="" width={64} height={64} />
          <div className={styles.info}>
            {isCurrent && <span className={styles.currentTag}>{labels.lblCurrentItem}</span>}
            <span aria-label={description} className={styles.title}>
              {description}
            </span>
            <div className={styles.detailsAndPriceContainer}>
              <div className={styles.details}>
                <span className={styles.partNumber}>
                  <span
                    aria-label={labels.lblProductPartNumber}
                    className={cx(styles.partNumberLabel, {
                      [styles.brakePadRotorLabel]: isBrakePad || isBrakeRotor,
                    })}
                  >
                    {labels.lblProductPartNumber}
                  </span>
                  {locale !== 'en-US' && ' '}
                  <span
                    aria-label={skuDetails.productPartNumber}
                    className={styles.partNumberValue}
                  >
                    {skuDetails.productPartNumber}
                  </span>
                </span>
                {locationFilter ? (
                  <span className={styles.location}>
                    <span
                      aria-label={labels.label_deals_sku_location}
                      className={cx(styles.locationLabel, {
                        [styles.brakePadRotorLabel]: isBrakePad || isBrakeRotor,
                      })}
                    >
                      {labels.label_deals_sku_location}
                    </span>
                    <span aria-label={locationFilter} className={styles.locationValue}>
                      {skuDetails.locationFilter}
                    </span>
                  </span>
                ) : null}

                {isBrakeRotor && studLugHoleQuantity ? (
                  <span className={styles.lugHoleQuantity}>
                    <span aria-label={labels.lugHoleQuantity} className={styles.lugHoleLabel}>
                      {labels.lugHoleQuantity}:
                    </span>
                    <span aria-label={studLugHoleQuantity} className={styles.lugHoleQuantityValue}>
                      {studLugHoleQuantity}
                    </span>
                  </span>
                ) : null}
                {productNotes ? (
                  <span className={styles.productNotes}>
                    <span
                      aria-label={labels.productNotesLabel}
                      className={styles.productNotesLabel}
                    >
                      {labels.productNotesLabel}:
                    </span>
                    <span aria-label="productNotes" className={styles.productNotesValue}>
                      {productNotes}
                    </span>
                  </span>
                ) : null}
                {isBrakePad && mountingHardwareIncluded ? (
                  <span className={styles.mountingHardware}>
                    <span
                      aria-label={labels.mountingHardwareIncluded}
                      className={styles.mountingHardwareLabel}
                    >
                      {labels.mountingHardwareIncluded}:
                    </span>
                    <span
                      aria-label={mountingHardwareIncluded}
                      className={styles.mountingHardwareValue}
                    >
                      {mountingHardwareIncluded}
                    </span>
                  </span>
                ) : null}
                {isBrakePad && padType ? (
                  <span className={styles.padType}>
                    <span aria-label={labels.padType} className={styles.padTypeLabel}>
                      {labels.padType}:
                    </span>
                    <span aria-label={padType} className={styles.padTypeValue}>
                      {padType}
                    </span>
                  </span>
                ) : null}
                <span className={styles.quantity}>
                  <span
                    aria-label={labels.label_deal_bundle_contraint_item_quantity}
                    className={styles.quantityLabel}
                  >
                    {labels.label_deal_bundle_contraint_item_quantity}
                  </span>
                  <span aria-label={`${minQty}`} className={styles.quantityValue}>
                    {minQty}
                  </span>
                </span>
              </div>
              <div>
                <Price
                  dealPrice={dealPrice}
                  wasDealPrice={wasDealPrice}
                  each={minQty > 1}
                  renderFreeInsteadOfZero={isNewDealDiscountExperienceEnabled}
                />
              </div>
            </div>
          </div>
        </div>

        {selected ? (
          <CheckIcon
            aria-hidden="true"
            className={cx(iconStyles.defaultIconStyle, styles.selectedIcon)}
          />
        ) : null}
      </div>
      {showSwapButton && (
        <button
          type="button"
          onClick={onSwapClick}
          onKeyDown={onSwapKeyDownHandler}
          className={styles.swapItem}
        >
          <div className={styles.swapIcon}>
            <Icon svg={SwapItem} />
          </div>
          {labels.lblSwapItem}
        </button>
      )}
    </>
  );
};
